<template>
  <div class="bg-white py-24 sm:py-32">
    <div class="mx-auto max-w-7xl px-6 lg:px-8">
      <div class="mx-auto max-w-2xl lg:mx-0">
        <!-- updated headers -->
        <h2 class="text-pretty text-4xl font-semibold tracking-tight text-gray-900 sm:text-5xl">{{
          t('home.blogNew.latest_blogs') }}</h2>
        <p class="mt-2 text-lg/8 text-gray-600">{{ t('home.blogNew.subtitle') }}</p>

        <!-- link to all blogs -->
        <div class="mt-4">
          <NuxtLink to="/blogs" class="text-sm font-semibold text-indigo-600 hover:text-indigo-500">
            {{ t('home.blogNew.view_all_blogs') }} <span aria-hidden="true">→</span>
          </NuxtLink>
        </div>
      </div>

      <div class="mx-auto mt-10 grid max-w-2xl grid-cols-1 gap-x-8 gap-y-16 border-t border-gray-200 pt-10
               sm:mt-16 sm:pt-16 lg:mx-0 lg:max-w-none lg:grid-cols-3">
        <article v-for="post in posts" :key="post.id" class="flex max-w-xl flex-col items-start justify-between">
          <div class="flex items-center gap-x-4 text-xs">
            <time :datetime="post.datetime" class="text-gray-500">{{ post.date }}</time>
            <a :href="post.category.href"
              class="relative z-10 rounded-full bg-gray-50 px-3 py-1.5 font-medium text-gray-600 hover:bg-gray-100">
              {{ post.category.title }}
            </a>
          </div>

          <div class="group relative grow">
            <h3 class="mt-3 text-lg/6 font-semibold text-gray-900 group-hover:text-gray-600">
              <a :href="post.href"><span class="absolute inset-0" />{{ post.title }}</a>
            </h3>
            <p class="mt-5 line-clamp-3 text-sm/6 text-gray-600">{{ post.description }}</p>
          </div>

          <div class="relative mt-8 flex items-center gap-x-4 justify-self-end">
  <div class="text-sm/6">
    <div class="font-semibold text-gray-900 flex flex-wrap items-center gap-2">
      <template v-for="(person, idx) in post.people" :key="person.href + idx">
        <NuxtLink :to="person.href" class="hover:underline">
          {{ person.name }}
        </NuxtLink>
        <span v-if="idx < post.people.length - 1" class="text-gray-400">·</span>
      </template>
    </div>
    <!-- <p class="text-gray-600">{{ post.author.role }}</p> -->
  </div>
</div>

        </article>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

function toIsoDate(d: any) {
  try { return new Date(d).toISOString().slice(0, 10) } catch { return '' }
}

const { t, locale } = useI18n()
const isZh = computed(() => {
  const v = (locale.value || '').toLowerCase()
  return v === 'zh' || v.startsWith('zh-hans') || v.startsWith('zh-hant')
})

const { data: raw } = await useAsyncData(
  'home-blogs',
  () => $fetch('/api/blogs', { headers: { 'X-Blog-Teaser': '1' } }),
  { server: true, default: () => [] }
)

const posts = computed(() => {
  const list = (raw.value as any[]) || []
  return list.map((p: any) => {
    const firstTag =
      Array.isArray(p.tags) && p.tags.length ? p.tags[0] : null

    // --- locale-aware author ---
    const authorName = isZh.value
      ? (p.author?.name_cn || p.author?.name || '认证医生')
      : (p.author?.name_en || p.author?.name || 'Certified Doctor')

    const authorHref = isZh.value
      ? (p.author?.href_cn || p.author?.href || '#')
      : (p.author?.href_en || p.author?.href || '#')

    // --- locale-aware collaborators ---
    const collabs = Array.isArray(p.collaborators) ? p.collaborators : []
    const people = [
      { name: authorName, href: authorHref },
      ...collabs.map((c: any) => ({
        name: isZh.value
          ? (c?.name_cn || c?.name || '协作医生')
          : (c?.name_en || c?.name || 'Collaborating Doctor'),
        href: isZh.value
          ? (c?.href_cn || c?.href || '#')
          : (c?.href_en || c?.href || '#')
      }))
    ]

    return {
      id: p._id,
      title: p.title,
      href: `/blogs/${p.slug || p._id}`,
      description: p.description,
      date: p.date,
      datetime: toIsoDate(p.updatedAt || p.createdAt || p.datetime),
      category: firstTag ? firstTag : { title: '健康', href: '/blogs' },
      author: {
        name: authorName,
        role: p.author?.role || '',
        href: authorHref,
        imageUrl: p.author?.imageUrl || ''
      },
      people
    }
  })
})
</script>

